import { useEffect, useState, useCallback } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Phone, PhoneOff, MessageCircle, Sparkles } from "lucide-react";

export function IVRPanel({ candidateId, canAct }) {
  const [calls, setCalls] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => api.get(`/ivr?candidate_id=${candidateId}`).then((r) => setCalls(r.data)).catch(() => {});
  useEffect(() => { load(); }, [candidateId]);

  const trigger = async (simulate_no_answer) => {
    setBusy(simulate_no_answer ? "no_answer" : "call");
    try {
      const { data } = await api.post("/ivr/trigger", { candidate_id: candidateId, simulate_no_answer });
      toast.success(simulate_no_answer
        ? "No-answer flow triggered — WhatsApp slots sent to candidate"
        : `IVR call complete: ${data.overall_ivr_score}/100 (${data.recommendation})`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const pick = async (ivrId, slot) => {
    try {
      await api.post("/ivr/pick-slot", { ivr_id: ivrId, slot });
      toast.success("Slot confirmed — IVR call will retry at the chosen time");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const latest = calls[0];

  return (
    <div className="border bg-white rounded-xl p-6" data-testid="ivr-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-bold"><Phone size={14} /> AI IVR Screening</div>
        {canAct && (
          <div className="flex gap-2">
            <button data-testid="ivr-trigger-call" onClick={() => trigger(false)} disabled={!!busy}
              className="flex items-center gap-1.5 text-xs bg-[#E05A47] text-white px-3 py-1.5 font-bold hover:bg-[#C74936] transition-colors duration-150 disabled:opacity-60">
              <Sparkles size={12} /> {busy === "call" ? "AI calling…" : "Run AI Call"}
            </button>
            <button data-testid="ivr-simulate-no-answer" onClick={() => trigger(true)} disabled={!!busy}
              className="flex items-center gap-1.5 text-xs border border-[#0A251D] text-[#0A251D] px-3 py-1.5 font-bold hover:bg-[#0A251D] hover:text-white transition-colors duration-150">
              <PhoneOff size={12} /> Simulate No-Answer
            </button>
          </div>
        )}
      </div>

      {calls.length === 0 && <div className="text-sm text-muted-foreground">No AI IVR calls yet. Trigger one to auto-screen the candidate.</div>}

      {latest?.status === "no_answer" && (
        <div className="border-2 border-amber-500 rounded-xl p-4 mb-3" data-testid="ivr-slot-picker">
          <div className="flex items-center gap-2 text-xs font-bold text-amber-800"><MessageCircle size={14} /> Candidate didn&apos;t answer — WhatsApp slot picker sent</div>
          <div className="grid grid-cols-2 gap-2 mt-3">
            {(latest.wa_slots || []).map((s) => (
              <button key={s} data-testid={`ivr-slot-${s}`} onClick={() => pick(latest.id, s)}
                className="text-xs font-semibold border px-3 py-2 hover:border-[#0A251D] transition-colors duration-150">
                {new Date(s).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
              </button>
            ))}
          </div>
          {latest.wa_slot_confirmed && <div className="text-xs mt-3 font-semibold text-emerald-700">✓ Slot confirmed: {new Date(latest.wa_slot_confirmed).toLocaleString()}</div>}
        </div>
      )}

      {latest?.status === "completed" && (
        <div>
          <div className="flex items-center gap-4">
            <div className="text-center shrink-0">
              <div className="font-heading font-black text-4xl text-[#0A251D]" data-testid="ivr-score">{latest.overall_ivr_score}</div>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">/ 100</div>
            </div>
            <div className="flex-1">
              <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-md font-bold ${
                latest.recommendation === "recommend_hr_round" ? "bg-emerald-100 text-emerald-800" :
                latest.recommendation === "not_recommended" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-800"}`}>
                {latest.recommendation?.replace(/_/g, " ")}
              </span>
              <p className="text-sm mt-2">{latest.summary}</p>
              <div className="flex gap-4 text-xs text-muted-foreground mt-2">
                <span>Comm {latest.communication_score}/100</span>
                <span>Tech {latest.technical_score}/100</span>
                <span>Motivation {latest.motivation_score}/100</span>
              </div>
              <div className="text-xs mt-1"><span className="text-muted-foreground">Salary ask:</span> <span className="font-semibold">{latest.salary_expectation || "—"}</span> · <span className="text-muted-foreground">Notice:</span> <span className="font-semibold">{latest.notice_period || "—"}</span> · <span className="text-muted-foreground">Location OK:</span> <span className="font-semibold">{latest.location_confirmed ? "Yes" : "No"}</span></div>
            </div>
          </div>
          {(latest.red_flags?.length > 0 || latest.strengths?.length > 0) && (
            <div className="grid md:grid-cols-2 gap-4 mt-4">
              {latest.strengths?.length > 0 && <div><div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold mb-1">Strengths</div>{latest.strengths.map((s, i) => <div key={i} className="text-sm py-0.5">+ {s}</div>)}</div>}
              {latest.red_flags?.length > 0 && <div><div className="text-[10px] uppercase tracking-widest text-red-700 font-bold mb-1">Red flags</div>{latest.red_flags.map((s, i) => <div key={i} className="text-sm py-0.5">− {s}</div>)}</div>}
            </div>
          )}
          <details className="mt-4" data-testid="ivr-transcript-details">
            <summary className="text-xs font-bold uppercase tracking-widest text-muted-foreground cursor-pointer hover:text-[#0A251D]">View transcript</summary>
            <div className="mt-3 space-y-3 max-h-96 overflow-y-auto pr-2">
              {latest.transcript?.map((t, i) => (
                <div key={i} className="border-l-2 border-[#0A251D] pl-3">
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-bold">Q{i + 1}</div>
                  <div className="text-sm font-semibold">{t.q}</div>
                  <div className="text-sm text-muted-foreground mt-1">A: {t.a}</div>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}
